import { useMutation } from '@tanstack/react-query';
import { AuthApi } from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription, Button } from '@up2stay/up2promo-ui';
import { LogOut } from 'lucide-react';
import { toast } from 'sonner';

import { resetSessionCaches } from '@/core/hooks/reset-session-caches';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';

export function ImpersonationBanner() {
  const dict = useDictionary();
  const bannerDict = dict.auth.impersonation;
  const user = useAuthStore((state) => state.user);
  const setTokens = useAuthStore((state) => state.setTokens);
  const setUser = useAuthStore((state) => state.setUser);
  const { navigateShell } = useNavigateShell();

  const stopImpersonation = useMutation({
    mutationFn: async () => {
      const response = await AuthApi.stopImpersonation();
      return response.data;
    },
    onSuccess: async (data) => {
      if (!data?.token || !data.refreshToken) {
        toast.error(bannerDict.exitError);
        return;
      }
      setTokens({
        accessToken: data.token,
        refreshToken: data.refreshToken,
      });
      resetSessionCaches();
      try {
        const meResponse = await AuthApi.me();
        setUser(meResponse.data);
      } catch {
        if (data.user) {
          setUser(data.user);
        }
      }
      navigateShell(shellPaths.home, { intent: 'switchTab' });
    },
    onError: () => {
      toast.error(bannerDict.exitError);
    },
  });

  const impersonation = user?.impersonation;
  if (!impersonation) {
    return null;
  }

  const targetName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email ||
    user?.id;

  const actorName =
    impersonation.actor.displayName?.trim() || impersonation.actor.id;

  return (
    <div className="min-w-0 px-3 pt-3">
      <Alert className="border-amber-400/60 bg-amber-100/50 dark:bg-amber-950/30">
        <AlertDescription className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="min-w-0 flex-1 break-words">
            {bannerDict.message
              .replace('{target}', targetName)
              .replace('{actor}', actorName)}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 self-start sm:self-auto"
            disabled={stopImpersonation.isPending}
            onClick={() => stopImpersonation.mutate()}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {bannerDict.exit}
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
